import {
  CatchBoundary,
  Outlet,
  createFileRoute,
  useLocation,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { asUserId } from '@leanin/shared/ids';
import {
  gateOnboardingComplete,
  memberPhotoUrl,
  sessionQuery,
  useSession,
} from '@/lib/use-session';
import { myPinsQuery, useMyPins } from '@/lib/queries/pins';
import { Shell } from '@/components/shell/Shell';
import { PublicShell } from '@/components/shell/PublicShell';
import { ReactivateAccount } from '@/components/auth/ReactivateAccount';
import { PageErrorState } from '@/components/shell/PageErrorState';
import { SessionErrorPage } from '@/components/shell/SessionErrorPage';
import { ViewerProvider, type ViewerIdentity } from '@/lib/viewer-context';
import { viewerWithAvatarSrc } from '@/lib/viewer';
import { reportRequestFailure } from '@/lib/report-error';

// Auth-optional layout. Mirrors `_authed.tsx` but does NOT redirect
// when the session resolves to null — anonymous visitors are first-
// class on routes mounted under `_public/*` (the public networks
// directory + per-network overview pages). When a session exists the
// authed `Shell` is rendered (sidebar, notifications, profile menu),
// matching `_authed.tsx`. When no session exists the slim `PublicShell`
// is rendered (top bar + Sign in / Join CTAs).
//
// The redirect-isolation pattern from `_authed.tsx` is intentionally
// NOT reused — we don't redirect, we just feed `null` through to the
// component and let it pick the chrome.
// The four ad-entry landings handle mid-onboarding visitors themselves
// (their beforeLoad routes them into the JOURNEY onboarding, honouring the
// ad link's intent) — the layout's generic gate would strip that intent by
// bouncing to the organic /onboarding/profile before the route runs.
// `/join/$slug` (join links) is deliberately NOT exempt.
function isEntryJourneyPath(pathname: string): boolean {
  return (
    pathname === '/join' ||
    pathname === '/start-a-circle' ||
    pathname === '/join-a-circle' ||
    pathname === '/join-a-network' ||
    pathname.startsWith('/join-a-network/')
  );
}

export const Route = createFileRoute('/_public')({
  beforeLoad: async ({ context, location }) => {
    let ctx;
    try {
      ctx = await context.queryClient.ensureQueryData(sessionQuery);
    } catch (err) {
      // Treat session-fetch failures as anonymous: degrade open rather than
      // bouncing to /sign-in. Reported, not shown.
      reportRequestFailure(context.reportError, err, {
        flow: 'auth.session_probe',
        guard: 'public_layout',
      });
      // Except on `/`, the bare domain returning members type and bookmark:
      // there the logged-out landing read as being signed out during deploys
      // and outages, so it fails closed, replacing the whole layout (chrome
      // included) with a retryable surface.
      if (location.pathname === '/') throw new SessionProbeFailedError(location.pathname);
      return { session: null, member: null };
    }
    if (ctx !== null) {
      // Signed-in viewer on a public route — still subject to the
      // onboarding gate. Without this, a new invitee whose webhook
      // redeem granted the leader role but who closed the tab before
      // tour completion could navigate directly to
      // `/networks/{theirId}` (under `_public`) and skip onboarding.
      // Anonymous viewers pass through; the gate is a no-op for them.
      if (!isEntryJourneyPath(location.pathname)) {
        gateOnboardingComplete(ctx);
      }
      // Pre-warm pins for the authed path — non-blocking; the shell
      // handles its own loading state.
      context.queryClient.prefetchQuery(myPinsQuery);
      return { session: ctx.session, member: ctx.member };
    }
    return { session: null, member: null };
  },
  errorComponent: PublicError,
  component: PublicLayout,
});

// Its own type so the error surface can tell an unknown auth state on `/`
// from any other failure in this layout itself: its load, or a render error
// in the layout or its shell.
class SessionProbeFailedError extends Error {
  readonly code = 'session_probe_failed';
  constructor(readonly pathname: string) {
    super(`Session probe failed on ${pathname}`);
    this.name = 'SessionProbeFailedError';
  }
}

function PublicError({ error, reset }: ErrorComponentProps) {
  // A fresh load rather than `router.invalidate()`: `/` treats only the
  // document's first load as arriving (see its route), and a recovered probe
  // must still send a remembered login on to /sign-in.
  if (error instanceof SessionProbeFailedError) {
    return <SessionErrorPage onRetry={() => window.location.reload()} />;
  }
  return <PageErrorState error={error} reset={reset} />;
}

function PublicLayout() {
  const { data: ctx } = useSession();
  const { pathname } = useLocation();
  // Anonymous viewers have no pins and the endpoint would 401 — only the
  // signed-in branch below renders the pinned-nav shell anyway.
  const { data: pins } = useMyPins({ enabled: Boolean(ctx) });
  // Same split as `_authed.tsx`: this layout's `errorComponent` covers the
  // layout itself (the session probe, the shell), and this boundary catches a
  // page's RENDER error below it, so a page bug shows a page error inside
  // whatever chrome the path has (none on the entry-journey landings).
  const page = (
    <CatchBoundary getResetKey={() => pathname} errorComponent={PageErrorState}>
      <Outlet />
    </CatchBoundary>
  );
  if (!ctx) {
    // Ad-entry landings render bare — the journey chrome (wordmark +
    // stepper) is the page's only branding, per design. Signed-in visitors
    // never reach this branch on those paths (their beforeLoad redirects).
    if (isEntryJourneyPath(pathname)) {
      return <ViewerProvider value={null}>{page}</ViewerProvider>;
    }
    return (
      <ViewerProvider value={null}>
        <PublicShell>{page}</PublicShell>
      </ViewerProvider>
    );
  }
  const { member } = ctx;
  // Same rule as `_authed.tsx`: a deactivated-within-window member gets the
  // reactivation prompt, not a signed-in shell where every write 403s.
  // Public routes are reachable while deactivated (deep links, ad journeys),
  // so without this the prompt was avoidable by simply not visiting /feed.
  if (member.deactivatedAt !== null) {
    return <ReactivateAccount deactivatedAt={member.deactivatedAt} />;
  }
  const photoUrl = memberPhotoUrl(member) ?? null;
  const viewer: ViewerIdentity = {
    id: asUserId(member.id),
    displayName: `${member.firstName} ${member.lastName}`.trim(),
    photoUrl,
  };
  const shellUser = {
    ...viewerWithAvatarSrc({
      id: member.id,
      firstName: member.firstName,
      lastName: member.lastName,
      photoUrl: photoUrl ?? undefined,
    }),
    platformRole: member.platformRole,
  };
  return (
    <ViewerProvider value={viewer}>
      <Shell user={shellUser} pins={pins ?? []} capabilities={ctx.capabilities}>
        {page}
      </Shell>
    </ViewerProvider>
  );
}
